import React, { useEffect, useRef } from 'react';
import './LowPolyBackground.css';

const LowPolyBackground = ({ children, className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Settings
    const CELL_SIZE = 68;
    const VARIANCE = 64;
    const WAVE_STRENGTH = 4.2;
    const SPEED = 0.007;
    const OVERLAP = 1.8;

    let time = 0;
    let cols, rows;
    let pointsGrid = [];

    function resize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;

      cols = Math.ceil(w / CELL_SIZE) + 6;
      rows = Math.ceil(h / CELL_SIZE) + 6;
      const offsetX = -CELL_SIZE * 1.2;
      const offsetY = -CELL_SIZE * 1.2;

      pointsGrid = [];
      for (let i = 0; i <= cols; i++) {
        pointsGrid[i] = [];
        for (let j = 0; j <= rows; j++) {
          const baseX = i * CELL_SIZE + offsetX;
          const baseY = j * CELL_SIZE + offsetY;
          
          const seedX = (i * 131071 + j * 524287) % 10000 / 10000;
          const seedY = (i * 297121 + j * 730111) % 10000 / 10000;
          const seedX2 = (i * 31091 + j * 19997) % 10000 / 10000;
          const seedY2 = (i * 70001 + j * 13331) % 10000 / 10000;
          
          const offX = (seedX - 0.5) * VARIANCE * 0.7 + (seedX2 - 0.5) * VARIANCE * 0.5;
          const offY = (seedY - 0.5) * VARIANCE * 0.7 + (seedY2 - 0.5) * VARIANCE * 0.5;
          
          const terrain = Math.sin(i * 0.23) * Math.cos(j * 0.21) * 0.8
                        + Math.sin(i * 0.57 + j * 0.43) * 0.3
                        + Math.sin(i * 0.12 + j * 0.67) * 0.2;
          let depth = (terrain + 1) / 2;
          depth = Math.min(0.98, Math.max(0.02, depth));
          
          pointsGrid[i][j] = { baseX, baseY, offX, offY, depth };
        }
      }
    }

    function getPoint(i, j, now) {
      const p = pointsGrid[i]?.[j];
      if (!p) return { x: 0, y: 0, depth: 0.5 };
      
      const waveX = Math.sin(i * 0.21 + now * 1.05) * WAVE_STRENGTH * 0.7
                  + Math.cos(j * 0.17 + now * 0.85) * WAVE_STRENGTH * 0.6
                  + Math.sin((i + j) * 0.13 + now * 0.55) * WAVE_STRENGTH * 0.4;
                  
      const waveY = Math.cos(j * 0.23 + now * 0.92) * WAVE_STRENGTH * 0.7
                  + Math.sin(i * 0.18 + now * 0.74) * WAVE_STRENGTH * 0.6
                  + Math.cos((i - j) * 0.15 + now * 0.63) * WAVE_STRENGTH * 0.4;
      
      return {
        x: p.baseX + p.offX + waveX,
        y: p.baseY + p.offY + waveY,
        depth: p.depth
      };
    }

    function getColor(x, y, depth, now) {
      const w = canvas.width;
      const h = canvas.height;
      let t = w > 0 ? x / w : 0.5;
      const yNorm = h > 0 ? y / h : 0.5;
      t = t + (yNorm - 0.5) * 0.08;
      t = Math.min(0.99, Math.max(0.01, t));

      const hueLeft = 300;
      const hueRight = 140;
      let hue = hueLeft * (1 - t) + hueRight * t;
      hue += (depth - 0.5) * 15;
      hue += Math.sin(now * 0.25) * 3;
      hue = (hue + 360) % 360;

      let sat = 70 + (depth - 0.5) * 12 + Math.sin(now * 0.45 + x * 0.01) * 4;
      sat = Math.min(90, Math.max(58, sat));

      let light = 46 + depth * 26 + Math.sin(now * 0.4 + y * 0.008) * 4;
      light = Math.min(80, Math.max(40, light));

      return `hsl(${hue}, ${sat}%, ${light}%)`;
    }

    function drawOverlappedTriangle(x1, y1, x2, y2, x3, y3, color) {
      const cx = (x1 + x2 + x3) / 3;
      const cy = (y1 + y2 + y3) / 3;
      
      const dx1 = x1 - cx, dy1 = y1 - cy;
      const len1 = Math.sqrt(dx1*dx1 + dy1*dy1) || 1;
      const dx2 = x2 - cx, dy2 = y2 - cy;
      const len2 = Math.sqrt(dx2*dx2 + dy2*dy2) || 1;
      const dx3 = x3 - cx, dy3 = y3 - cy;
      const len3 = Math.sqrt(dx3*dx3 + dy3*dy3) || 1;
      
      const scale = 1 + OVERLAP / Math.max(len1, len2, len3, 1);
      
      const ox1 = cx + (x1 - cx) * scale;
      const oy1 = cy + (y1 - cy) * scale;
      const ox2 = cx + (x2 - cx) * scale;
      const oy2 = cy + (y2 - cy) * scale;
      const ox3 = cx + (x3 - cx) * scale;
      const oy3 = cy + (y3 - cy) * scale;
      
      ctx.beginPath();
      ctx.moveTo(ox1, oy1);
      ctx.lineTo(ox2, oy2);
      ctx.lineTo(ox3, oy3);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    }

    function draw(now) {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      ctx.imageSmoothingEnabled = false;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const p00 = getPoint(i, j, now);
          const p10 = getPoint(i+1, j, now);
          const p01 = getPoint(i, j+1, now);
          const p11 = getPoint(i+1, j+1, now);

          const cx = (p00.x + p10.x + p01.x + p11.x) / 4;
          const cy = (p00.y + p10.y + p01.y + p11.y) / 4;
          const avgDepth = (p00.depth + p10.depth + p01.depth + p11.depth) / 4;

          const color1 = getColor(cx, cy, avgDepth, now);
          drawOverlappedTriangle(p00.x, p00.y, p10.x, p10.y, p01.x, p01.y, color1);

          const depth2 = (p11.depth + p10.depth + p01.depth) / 3;
          const color2 = getColor(cx + 3, cy + 3, depth2, now + 0.2);
          drawOverlappedTriangle(p11.x, p11.y, p10.x, p10.y, p01.x, p01.y, color2);
        }
      }
    }

    function animate() {
      time += SPEED;
      draw(time);
      requestAnimationFrame(animate);
    }

    window.addEventListener('resize', resize);
    resize();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="lowpoly-container">
      <canvas 
        ref={canvasRef} 
        className="lowpoly-canvas"
      />
      <div className="lowpoly-content">
        {children}
      </div>
    </div>
  );
};

export default LowPolyBackground;